import type { CreateFeedbackInput, FeedbackContextType } from '@shared/domain/feedback/Feedback';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { FEEDBACK_RATING_DOWN, FEEDBACK_RATING_UP } from '@shared/domain/feedback/Feedback';
import { useApi } from '@shared/services/api';
import { useSnackbar } from '@shared/services/SnackbarService/SnackbarServiceContext';
import { useState } from 'react';

/**
 * Chat answers run long and the API caps serialized `contextData` at 10 kB, so
 * callers clip the text they attach. A clipped answer still says what the user
 * was reacting to; a rejected POST says nothing at all.
 */
export function clipForFeedback(text: string, max = 4000): string {
  return text.length <= max ? text : `${text.slice(0, max)}…`;
}

export interface HoverFeedbackProps {
  /** The content being rated. Required by the `overlay` variant. */
  children?: React.ReactNode;
  /**
   * Snapshot of what is being rated — include the item's own text, so the
   * record stays readable once the signal is re-extracted or deleted.
   */
  contextData: Record<string, unknown>;
  /** Identifier of the thing being rated, within `contextType`. */
  contextKey?: number | string;
  contextType: FeedbackContextType;
  /** Names the item in the thumbs-down prompt, e.g. "evidence quote". */
  itemLabel: string;
  /**
   * `overlay` (the default) wraps `children` and pins the thumbs to their
   * top-right, revealing them on hover. `inline` renders the buttons on their
   * own, for a caller that already has a hover-revealed action row of its own —
   * the chat messages, which sit beside Save and Copy.
   */
  variant?: 'inline' | 'overlay';
}

/**
 * Thumbs up / thumbs down on a piece of AI-generated content, staying out of
 * the way until the user hovers it.
 *
 * Thumbs up posts immediately. Thumbs down opens a dialog asking what was
 * wrong, because a bare "this is bad" is rarely actionable — but the rating is
 * still posted if the user skips the explanation, so the signal is never lost
 * to someone who doesn't feel like typing.
 */
export function HoverFeedback({
  children,
  contextData,
  contextKey,
  contextType,
  itemLabel,
  variant = 'overlay',
}: HoverFeedbackProps): React.ReactElement {
  const api = useApi();
  const { showSnackbar } = useSnackbar();
  const [comment, setComment] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rated, setRated] = useState<null | number>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (rating: number, commentText?: string): Promise<void> => {
    if (submitting) return;
    setSubmitting(true);

    const body: CreateFeedbackInput = {
      comment: commentText?.trim() || null,
      contextData,
      contextKey: contextKey == null ? null : String(contextKey),
      contextType,
      rating,
    };
    const response = await api.post('/feedback', body);

    setSubmitting(false);

    if (response?.error) {
      showSnackbar(response.message || 'Failed to send feedback.', 'error');
      return;
    }

    setDialogOpen(false);
    setComment('');
    setRated(rating);
    showSnackbar('Thanks for the feedback.', 'success');
  };

  const thumbs = (
    <>
      <Tooltip title={`This ${itemLabel} is useful`} arrow>
        <span>
          <IconButton
            size="small"
            aria-label={`This ${itemLabel} is useful`}
            disabled={submitting || rated != null}
            onClick={() => void submit(FEEDBACK_RATING_UP)}
            sx={{ p: 0.25 }}
          >
            {rated === FEEDBACK_RATING_UP ? (
              <ThumbUpAltIcon sx={{ fontSize: 14, color: 'success.main' }} />
            ) : (
              <ThumbUpOffAltIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={`Something is wrong with this ${itemLabel}`} arrow>
        <span>
          <IconButton
            size="small"
            aria-label={`Something is wrong with this ${itemLabel}`}
            disabled={submitting || rated != null}
            onClick={() => setDialogOpen(true)}
            sx={{ p: 0.25 }}
          >
            {rated === FEEDBACK_RATING_DOWN ? (
              <ThumbDownAltIcon sx={{ fontSize: 14, color: 'error.main' }} />
            ) : (
              <ThumbDownOffAltIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </>
  );

  const dialog = (
    <Dialog open={dialogOpen} onClose={() => !submitting && setDialogOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>What&rsquo;s wrong with this {itemLabel}?</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={comment}
          placeholder="Inaccurate, irrelevant, out of date, about the wrong company…"
          inputProps={{ 'aria-label': `What is wrong with this ${itemLabel}?` }}
          onChange={(event) => setComment(event.target.value)}
          disabled={submitting}
          margin="dense"
          // The dialog only opens on an explicit thumbs down, so taking focus
          // is what the user just asked for.
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        {/* Skip still posts the rating — losing it because the user didn't
            want to write is worse than an unexplained thumbs down. */}
        <Button disabled={submitting} onClick={() => void submit(FEEDBACK_RATING_DOWN)}>
          Skip
        </Button>
        <Button
          variant="contained"
          disabled={submitting || !comment.trim()}
          onClick={() => void submit(FEEDBACK_RATING_DOWN, comment)}
        >
          {submitting ? 'Sending…' : 'Send feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // The caller supplies the hover reveal in this variant — the chat message
  // action rows already have one, shared with their Save and Copy buttons.
  if (variant === 'inline') {
    return (
      <Stack direction="row" alignItems="center">
        {thumbs}
        {dialog}
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        // Hidden until hover (or keyboard focus) so a dense drawer doesn't turn
        // into a wall of thumbs. Opacity rather than display, so the buttons
        // stay in the tab order and the layout never shifts.
        '&:hover .hover-feedback__actions, &:focus-within .hover-feedback__actions': {
          opacity: 1,
        },
      }}
    >
      {children}

      <Stack
        className="hover-feedback__actions"
        direction="row"
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          // Hidden again once rated — a pinned-open control sits on top of the
          // content it was rating (the priority score, on an opportunity card).
          // The filled thumb still shows the standing rating on the next hover.
          opacity: 0,
          transition: 'opacity 120ms',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {thumbs}
      </Stack>

      {dialog}
    </Box>
  );
}
